import { Link } from 'react-router-dom'
import { ChevronRight, ArrowRight } from 'lucide-react'
import { SECONDARY_CTA_TEXT } from '../../../../config/cta'
import { DEMO_SURVEY_URL } from '../../../../config/links'
import Seo from '../../../../components/Seo'

const benefits = [
  {
    title: 'Early warning',
    description:
      'Identify at-risk accounts weeks before renewal conversations, not during them. Your team acts when there is still time to change the outcome.',
  },
  {
    title: 'Root causes, not symptoms',
    description:
      'Understand why customers feel the way they do, not just that they do. Outreach is targeted and credible because it is based on what the customer actually said.',
  },
  {
    title: 'Across every account',
    description:
      'Qualitative depth across your full customer base, not just the accounts that escalate or request a call. Every customer gets a real conversation.',
  },
  {
    title: 'Alongside your existing stack',
    description:
      'PrismScope layers on top of your current NPS or CSAT programs. No rip and replace. Running in parallel within days.',
  },
]

export default function ChurnPrevention() {
  return (
    <div>
      <Seo
        title="Churn Prevention"
        description="Detect at-risk accounts early and intervene before a low score becomes a cancellation. Proactive churn-prevention conversations."
        path="/feedback-loops/customer/churn/prevention"
      />

      {/* Breadcrumbs */}
      <div className="bg-white border-b border-intelligence-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center text-sm text-intelligence-500">
            <Link to="/" className="hover:text-intelligence-900">
              Home
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/feedback-loops" className="hover:text-intelligence-900">
              Use Cases
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/feedback-loops/customer/churn" className="hover:text-intelligence-900">
              Churn Research
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-intelligence-900">Prevention</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-intelligence-50 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold tracking-widest uppercase text-accent-600 mb-3">
            Churn Prevention
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-intelligence-900 mb-4">
            You had a 7. Three months later, they churned.
          </h1>
          <p className="text-xl text-intelligence-600 max-w-2xl">
            Nothing in your survey data would have flagged it. PrismScope surfaces the situations
            that lead to churn before they become decisions.
          </p>
        </div>
      </section>

      {/* Scenario Opener */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl border-l-4 border-accent-500 p-8 md:p-10 shadow-sm">
            <p className="text-lg text-intelligence-700 italic leading-relaxed">
              &ldquo;We had a 7 out of 10 Net Promoter Score from an account that churned three
              months later. When we went back through their survey responses, there was nothing that
              would have flagged them. They gave short answers. Nothing alarming. What we did not
              know was that their admin had been struggling with a workflow issue for months, and
              they never found the right place to say it.&rdquo;
            </p>
          </div>

          <div className="mt-12 space-y-6 text-lg text-intelligence-600 leading-relaxed">
            <p>
              This is not an unusual story. It is the most common story Customer Success leaders
              tell when they talk about churn that surprised them. The score looked fine. The
              open-ended comments were short. And somewhere underneath all of that, a real problem
              was building that no one could see.
            </p>
            <p>
              Static surveys are not designed to find that kind of problem. They ask every customer
              the same question and accept whatever two sentences come back. If a customer does not
              know how to articulate what is wrong, or does not feel that a survey is the right
              place to raise it, the signal is lost. Your team inherits the interpretation
              problem&mdash;guessing at what customers meant rather than knowing.
            </p>
            <p>
              By the time dissatisfaction becomes visible through usage decline, an escalation, or a
              tense renewal conversation, the opportunity to act has already narrowed.
            </p>
          </div>

          {/* Who This Is For */}
          <div className="mt-10 bg-primary-50 rounded-xl border border-primary-200 p-6 md:p-8">
            <h3 className="text-sm font-semibold tracking-widest uppercase text-primary-600 mb-2">
              Who this is for
            </h3>
            <p className="text-lg text-intelligence-700">
              Customer Success leaders at mid-market B2B SaaS companies managing 50 to 500 accounts
              who already run NPS or CSAT programs and are running into the limits of what those
              programs can tell them.
            </p>
          </div>
        </div>
      </section>

      {/* What PrismScope Does */}
      <section className="bg-intelligence-50 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-8">
            What PrismScope does about it
          </h2>
          <div className="space-y-6 text-lg text-intelligence-600 leading-relaxed">
            <p>
              PrismScope opens an adaptive follow-up conversation the moment a customer submits
              their survey response. It asks follow-up questions based on what each customer
              actually says, not what you predicted they would say. It probes when something
              important surfaces. It asks the questions a skilled Customer Success leader would ask
              if they could talk to every customer personally.
            </p>
            <p>
              The result is not a slightly better score. It is specific, contextual intelligence
              about what is actually happening for each customer: what broke, how long it has been
              broken, whether the champion is still there, and what it would take to fix it.
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            {benefits.map((benefit) => (
              <div
                key={benefit.title}
                className="bg-white rounded-xl border border-intelligence-200 p-6"
              >
                <h4 className="text-lg font-semibold text-intelligence-900 mb-2">
                  {benefit.title}
                </h4>
                <p className="text-intelligence-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before / After */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-intelligence-900 rounded-xl p-8 md:p-10">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-xs font-semibold tracking-widest uppercase text-intelligence-400 mb-3">
                  Before
                </p>
                <p className="text-intelligence-300 leading-relaxed">
                  Your team reacts to churn after it becomes visible. Risk surfaces through usage
                  decline, escalations, or a tense renewal conversation when it is too late to act
                  meaningfully.
                </p>
              </div>
              <div className="hidden md:flex items-center justify-center">
                <ArrowRight className="w-8 h-8 text-accent-400" />
              </div>
              <div className="md:col-start-2 md:row-start-1">
                <p className="text-xs font-semibold tracking-widest uppercase text-accent-400 mb-3">
                  After
                </p>
                <p className="text-intelligence-300 leading-relaxed">
                  Your team detects risk early through real customer intelligence. Customer Success
                  leaders walk into renewal conversations knowing specifically what each customer
                  has been struggling with.
                </p>
              </div>
            </div>
          </div>

          {/* Testimonial */}
          <div className="mt-12 bg-primary-50 rounded-xl border-l-4 border-primary-500 p-8">
            <p className="text-lg text-intelligence-700 italic leading-relaxed">
              &ldquo;One of our largest accounts gave us a 6 in their last NPS cycle. We would
              normally have logged it, maybe scheduled a check-in. PrismScope&apos;s conversation
              revealed that their primary champion had left two months earlier, the new admin was
              undertrained, and a critical report they relied on weekly had been broken since their
              last upgrade. We were in their office within a week. They renewed and
              expanded.&rdquo;
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary-600 py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Stop reacting to churn. Start detecting it early.
          </h2>
          <p className="text-lg text-primary-100 mb-8">
            Book a demo and we will walk you through a live example using a churn prevention
            scenario relevant to your team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={DEMO_SURVEY_URL}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-primary-600 bg-white hover:bg-primary-50 rounded-lg shadow-md transition-colors"
            >
              {SECONDARY_CTA_TEXT}
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white border-2 border-white/50 hover:border-white hover:bg-white/10 rounded-lg transition-colors"
            >
              Schedule a Call
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
